import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, Sparkles } from 'lucide-react';
import { Signature, useSignature } from './SignatureSystem';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [navPower, setNavPower] = useState<'dormant' | 'active' | 'supercharged'>('dormant');
  const [showEffect, setShowEffect] = useState(false);

  const handleSignatureClick = () => {
    setNavPower(current => {
      if (current === 'dormant') return 'active';
      if (current === 'active') {
        setShowEffect(true);
        setTimeout(() => setShowEffect(false), 2000);
        return 'supercharged';
      }
      return 'dormant';
    });
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/30 backdrop-blur-md border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center gap-2">
              <span className="font-poppins text-xl font-bold text-white">
                soul<span className="text-sc-blue">cloude</span>
              </span>
              <div 
                className="flex items-center gap-1 cursor-pointer group"
                onClick={handleSignatureClick}
              >
                <Signature type="sxsc" size="sm" showEffects={true} power={navPower} className="navbar" />
                <Sparkles className={`w-3 h-3 text-sc-mint transition-all duration-300 ${navPower === 'supercharged' ? 'scale-125 animate-pulse' : 'group-hover:scale-110'}`} />
                <Signature type="hxsc" size="sm" showEffects={true} power={navPower} className="navbar" />
                {showEffect && (
                  <div className="absolute inset-0 animate-pulse-fast bg-gradient-to-r from-sc-blue/10 via-sc-mint/10 to-sc-violet/10 rounded-full blur-xl" />
                )}
              </div>
            </Link>
          </div>
          
          {/* Desktop menu */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-4">
              <Link to="/" className="px-3 py-2 text-sm font-medium text-white hover:text-sc-blue transition-colors">
                Home
              </Link>
              <Link to="/souls" className="px-3 py-2 text-sm font-medium text-white hover:text-sc-blue transition-colors">
                Souls
              </Link>
              <Link to="/about" className="px-3 py-2 text-sm font-medium text-white hover:text-sc-blue transition-colors">
                About Us
              </Link>
              <Link to="/learn" className="px-3 py-2 text-sm font-medium text-white hover:text-sc-blue transition-colors">
                Learn
              </Link>
              <Link to="/trust" className="px-3 py-2 text-sm font-medium text-white hover:text-sc-blue transition-colors">
                Trust & Safety
              </Link>
              <Link to="/owner" className="px-3 py-2 text-sm font-medium text-white hover:text-sc-blue transition-colors flex items-center gap-1">
                Owner
                <div className="flex items-center gap-1 ml-1">
                  <Signature type="sxsc" size="sm" showEffects={false} className="navbar" />
                  <Signature type="hxsc" size="sm" showEffects={false} className="navbar" />
                </div>
              </Link>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              onClick={toggleMenu}
              variant="ghost"
              size="icon"
              className="inline-flex items-center justify-center text-white hover:text-sc-blue"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden glass-card m-2 rounded-lg">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link 
              to="/" 
              className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-black/30 hover:text-sc-blue"
              onClick={() => setIsMenuOpen(false)}
            >
              Home
            </Link>
            <Link 
              to="/souls" 
              className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-black/30 hover:text-sc-blue"
              onClick={() => setIsMenuOpen(false)}
            >
              Souls
            </Link>
            <Link 
              to="/about" 
              className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-black/30 hover:text-sc-blue"
              onClick={() => setIsMenuOpen(false)}
            >
              About Us
            </Link>
            <Link 
              to="/learn" 
              className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-black/30 hover:text-sc-blue"
              onClick={() => setIsMenuOpen(false)}
            >
              Learn
            </Link>
            <Link 
              to="/trust" 
              className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-black/30 hover:text-sc-blue"
              onClick={() => setIsMenuOpen(false)}
            >
              Trust & Safety
            </Link>
            <Link 
              to="/owner" 
              className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-black/30 hover:text-sc-blue"
              onClick={() => setIsMenuOpen(false)}
            >
              <div className="flex items-center gap-2">
                Owner
                <div className="flex items-center gap-1">
                  <Signature type="sxsc" size="sm" showEffects={false} className="navbar" />
                  <Signature type="hxsc" size="sm" showEffects={false} className="navbar" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
